import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Accessibility,
  CheckCircle,
  Mail,
  Phone,
  FileText
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useContent } from '../contexts/ContentContext';

export const WebsiteAccessibility = () => {
  const { global } = useContent();

  return (
    <div data-testid="accessibility-page">
      <Helmet>
        <title>Website Accessibility Statement | A-Quality Pool Service</title>
        <meta name="description" content="A-Quality Pool Service is committed to ensuring digital accessibility. Learn about our efforts to meet WCAG guidelines." />
        <link rel="canonical" href="https://a-qualitypools.net/website-accessibility" />
      </Helmet>
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 to-slate-900" />
        
        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="mx-auto w-16 h-16 bg-sky-500 rounded-2xl flex items-center justify-center mb-6">
              <Accessibility className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Website Accessibility Statement
            </h1>
            <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto">
              Last Updated: February 27, 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            
            {/* Our Commitment */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Our Commitment to Accessibility
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We are committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying relevant accessibility standards to enhance usability and inclusivity. Our goal is to provide a website that is accessible to the widest possible audience, regardless of technology or ability.
              </p>
            </motion.div>

            {/* Accessibility Standards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Accessibility Standards
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA, which are widely recognized as the industry standard for website accessibility. Accessibility is an ongoing process, and we are actively working toward improving the accessibility and usability of our website.
              </p>
            </motion.div>

            {/* Technology & Tools */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Accessibility Technology & Tools
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                To support our accessibility efforts, we have deployed industry-standard accessibility technology designed to enhance usability and assist visitors with disabilities. These tools may provide features such as:
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  'Screen reader enhancements',
                  'Keyboard navigation improvements',
                  'Color contrast adjustments',
                  'Text size and spacing controls',
                  'Focus indicators',
                  'Additional accessibility interface options'
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-sky-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-slate-600 leading-relaxed">
                This technology helps us improve accessibility while we continue broader auditing, remediation, and development efforts.
              </p>
            </motion.div>

            {/* Ongoing Improvement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Ongoing Improvement
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Accessibility is an ongoing initiative. We are:
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  'Reviewing site content and functionality regularly',
                  'Working with developers and accessibility tools',
                  'Identifying and remediating barriers',
                  'Incorporating accessibility into future design and development'
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-sky-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-slate-600 leading-relaxed">
                We understand accessibility is a continuous journey and are committed to making improvements over time.
              </p>
            </motion.div>

            {/* Third-Party Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Third-Party Content
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Some areas of the website may include third-party content, plugins, or integrations that are not fully controlled by us. While we encourage vendors to provide accessible content, we cannot guarantee accessibility of third-party features. However, we welcome feedback regarding any issues encountered.
              </p>
            </motion.div>

            {/* Feedback & Assistance */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Feedback & Assistance
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                If you experience difficulty accessing any part of this website, need assistance, or would like to request content in an alternative format, please contact us.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                Please include:
              </p>
              <ul className="space-y-2 mb-6">
                {[
                  'The page URL',
                  'Description of the issue',
                  'Your preferred contact method'
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-sky-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
              
              <Card className="bg-slate-50 border-0">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Contact:</h3>
                  <div className="space-y-3">
                    <a 
                      href={`tel:${global.phone}`}
                      className="flex items-center text-slate-600 hover:text-sky-500 transition-colors"
                    >
                      <Phone className="h-5 w-5 mr-3 text-sky-500" />
                      {global.phone}
                    </a>
                    <a 
                      href={`mailto:${global.email}`}
                      className="flex items-center text-slate-600 hover:text-sky-500 transition-colors"
                    >
                      <Mail className="h-5 w-5 mr-3 text-sky-500" />
                      {global.email}
                    </a>
                    <Link 
                      to="/contact"
                      className="flex items-center text-slate-600 hover:text-sky-500 transition-colors"
                    >
                      <FileText className="h-5 w-5 mr-3 text-sky-500" />
                      Visit our Contact Us Page
                    </Link>
                  </div>
                </CardContent>
              </Card>
              
              <p className="text-slate-600 leading-relaxed mt-4">
                We aim to respond within a reasonable timeframe and will make reasonable efforts to provide accessible alternatives.
              </p>
            </motion.div>

            {/* Good Faith Efforts */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Good Faith Efforts
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We are making good-faith efforts to comply with accessibility standards and improve usability for individuals with disabilities. Despite our efforts, some content may not yet fully meet the strictest accessibility standards due to the evolving nature of technology and ongoing updates. We appreciate your understanding as we continue improving accessibility.
              </p>
            </motion.div>

            {/* Terms & Conditions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Terms & Conditions — Accessibility Clause
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We are committed to providing an accessible website experience for all users and strive to follow recognized accessibility standards, including WCAG guidelines. We have implemented industry-standard accessibility tools and continue to review, update, and improve our website to enhance accessibility. Because accessibility is an ongoing process, some areas of the website may not yet fully conform to all accessibility standards. If you encounter accessibility barriers, please contact us so we can provide assistance, offer alternative access, and address the issue where feasible. Nothing in these Terms is intended to limit any rights available under applicable accessibility laws.
              </p>
            </motion.div>

          </div>
        </div>
      </section>
    </div>
  );
};
